import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, VideoCamera, DownloadSimple, Copy, PaperPlaneTilt, SignOut, Share, Coins, ArrowSquareOut } from "@phosphor-icons/react";
import ProtectedVideo from "@/components/ProtectedVideo";
import MuralPopup from "@/components/MuralPopup";
import { publicUrl } from "@/lib/publicUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function memberFetch(path, opts = {}) {
  const token = localStorage.getItem("vb_member_token");
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Erro");
  return res.json();
}

const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));

export default function MemberArea() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [active, setActive] = useState(0);
  const [shareEmails, setShareEmails] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("vb_member_token")) { nav("/member/login"); return; }
    memberFetch("/member/content").then(setData).catch(() => nav("/member/login"));
    memberFetch("/member/earnings").then(setEarnings).catch(() => {});
  }, [nav]);

  const logout = () => { localStorage.removeItem("vb_member_token"); localStorage.removeItem("vb_member"); nav("/member/login"); };

  const current = data?.content?.[active];
  const affiliateFullLink = current ? publicUrl(current.affiliate_link) : "";

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Link copiado"); };
  const share = async () => {
    if (!shareEmails) return toast.error("Adicione ao menos 1 e-mail");
    const emails = shareEmails.split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
    setSharing(true);
    try {
      const r = await memberFetch("/member/share", { method: "POST", body: JSON.stringify({ funnel_id: current.id, to_emails: emails, message: shareMsg }) });
      toast.success(`Link enviado para ${r.sent} pessoa(s)`);
      setShareEmails(""); setShareMsg("");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setSharing(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-white/60">Carregando...</div>;

  return (
    <div className="min-h-screen flex">
      <aside className="w-72 shrink-0 border-r border-[var(--border)] p-6 flex flex-col gap-1 sticky top-0 h-screen">
        <Link to="/member" className="flex items-center gap-2 mb-8">
          <Lightning size={26} weight="fill" color="var(--accent)" />
          <div>
            <div className="font-display font-black text-lg leading-none">MERCADOPAGU</div>
            <div className="label-caps text-[10px] leading-none mt-1">Área do aluno</div>
          </div>
        </Link>
        <div className="label-caps mb-2">Meus cursos</div>
        {data.content.length === 0 && <div className="text-white/50 text-sm">Você ainda não comprou nenhum curso.</div>}
        {data.content.map((c, i) => (
          <button key={c.id} onClick={() => setActive(i)} data-testid={`member-course-${i}`} className={`flex items-center gap-3 px-3 py-2 text-sm text-left rounded-sm transition ${active === i ? "bg-[var(--accent)] text-black font-semibold" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
            <VideoCamera size={16} /> {c.title}
          </button>
        ))}
        <div className="mt-auto pt-6 border-t border-[var(--border)]">
          <div className="label-caps mb-2">Conta</div>
          <div className="text-sm font-semibold">{data.member.name}</div>
          <div className="text-xs text-white/50">{data.member.email}</div>
          <div className="text-xs text-white/40 mt-2">Código afiliado: <code className="text-[var(--accent)]">{data.member.affiliate_code}</code></div>
          <button onClick={logout} data-testid="member-logout" className="mt-4 flex items-center gap-2 text-xs text-white/60 hover:text-[var(--danger)]">
            <SignOut size={14} /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 md:p-10 max-w-[1200px]">
        {!current ? (
          <div className="text-white/60">Selecione um curso.</div>
        ) : (
          <div className="fadeUp">
            {earnings && earnings.sales_count > 0 && (
              <div className="card-flat p-4 mb-6 flex items-center justify-between border-[var(--accent)]" data-testid="member-earnings-banner">
                <div className="flex items-center gap-3">
                  <Coins size={28} color="var(--accent)" weight="fill" />
                  <div>
                    <div className="label-caps">Você já ganhou como afiliado</div>
                    <div className="font-display font-black text-2xl text-[var(--accent)]">{brl(earnings.total_earned)}</div>
                  </div>
                </div>
                <div className="text-xs text-white/60">{earnings.sales_count} venda(s) por indicação</div>
              </div>
            )}
            <div className="label-caps mb-2">Curso</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-8" data-testid="member-course-title">{current.title}</h1>

            {/* 1) VÍDEOS EM CIMA */}
            {current.delivery_videos?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {current.delivery_videos.map((v, i) => (
                  <div key={v.url || `dv-${i}`} className="card-flat p-4" data-testid={`member-video-${i}`}>
                    <div className="font-display font-bold text-lg mb-3">{v.name || `Vídeo ${i + 1}`}</div>
                    <ProtectedVideo url={v.url} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-white/50 text-sm mb-8">Nenhum vídeo neste curso ainda.</div>
            )}

            {/* 2) ARQUIVOS ABAIXO DO VÍDEO */}
            {current.files?.length > 0 && (
              <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="member-files">
                {current.files.map((f, i) => (
                  <div key={`file-${i}`} className="card-flat p-4 flex items-center justify-between" data-testid={`member-file-${i}`}>
                    <div className="min-w-0 flex-1 mr-3">
                      <div className="label-caps">{i === 0 ? "Material principal" : `Anexo ${i + 1}`}</div>
                      <div className="font-display font-bold text-sm text-white/90 mt-1 truncate">{f.name || "Arquivo"}</div>
                      {f.description && <div className="text-xs text-white/60 mt-1 line-clamp-2">{f.description}</div>}
                    </div>
                    <a href={f.url} target="_blank" rel="noreferrer" className="btn-primary flex items-center gap-2 shrink-0" data-testid={`member-file-btn-${i}`}>
                      <DownloadSimple size={16} weight="bold" /> Baixar
                    </a>
                  </div>
                ))}
              </div>
            )}

            {/* 3) BOTÕES DE LINK — MODERNOS, ABAIXO DO VÍDEO */}
            {current.buttons?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-10" data-testid="member-buttons">
                {current.buttons.map((b, i) => {
                  const isOutline = b.style === "outline";
                  const cls = isOutline
                    ? "border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black"
                    : "bg-[var(--accent)] text-black hover:brightness-110";
                  return (
                    <a
                      key={`btn-${i}`}
                      href={b.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`member-button-${i}`}
                      className={`${cls} inline-flex items-center gap-2 px-5 py-3 rounded-sm font-bold text-sm transition-all hover:scale-[1.02] active:scale-95`}
                    >
                      <ArrowSquareOut size={16} weight="bold" />
                      {b.label || "Abrir link"}
                    </a>
                  );
                })}
              </div>
            )}

            {/* Affiliate share */}
            <div className="card-flat p-6">
              <div className="label-caps flex items-center gap-2 mb-2"><Share size={12} /> Ganhe indicando</div>
              <h2 className="font-display font-bold text-2xl mb-4">Compartilhe este curso e ganhe</h2>
              <p className="text-white/60 text-sm mb-4">Seu link exclusivo (com código de afiliado):</p>
              <div className="flex gap-2 mb-6">
                <div className="flex-1 text-xs font-mono bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-3 py-2 text-white/70 truncate" data-testid="member-aff-link">{affiliateFullLink}</div>
                <button onClick={() => copy(affiliateFullLink)} className="btn-ghost text-xs flex items-center gap-1"><Copy size={14} /> Copiar</button>
              </div>

              <div className="label-caps mb-2">Enviar por e-mail</div>
              <textarea rows={2} placeholder="E-mails separados por vírgula" value={shareEmails} onChange={(e) => setShareEmails(e.target.value)} data-testid="member-share-emails" className="w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 mb-3 focus:border-[var(--accent)] outline-none" />
              <textarea rows={3} placeholder="Sua mensagem (opcional)" value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} data-testid="member-share-msg" className="w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 mb-3 focus:border-[var(--accent)] outline-none" />
              <button onClick={share} disabled={sharing} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="member-share-send">
                <PaperPlaneTilt size={16} weight="fill" /> {sharing ? "Enviando..." : "Enviar convite"}
              </button>
            </div>
          </div>
        )}
      </main>
      <MuralPopup audience="member" />
    </div>
  );
}
